import { useMemo } from "react";
import { Badge, Button, Stack, Tooltip, Typography } from "@altimateai/lego";
import { IconX } from "@tabler/icons-react";
import { formatNumber } from "@/uiCore";
import { DataTable } from "@/components/tables";
import { FilterType, TableHeaderType, TableRowItem } from "@/components/tables/types";
import { BreakdownItem } from "../hooks/useChartData";
import { formatProductName } from "../helpers";

interface Props {
  items: BreakdownItem[];
  currency: string;
  columnLabel: string;
  nameFilter: string[];
  onFilterChange: (values: string[]) => void;
  onRemoveFilter: (value: string) => void;
}

interface TableBreakdownItem extends TableRowItem {
  name: string;
  estimated_cost: number;
  pct_of_total: number;
  total_dbus: number;
}

const CostBreakdownTable = ({
  items,
  currency,
  columnLabel,
  nameFilter,
  onFilterChange,
  onRemoveFilter,
}: Props) => {
  const tableData: TableBreakdownItem[] = useMemo(
    () => items.map(item => ({ ...item, id: item.name })),
    [items]
  );

  const nameOptions = useMemo(
    () =>
      items.map(item => ({
        value: item.name,
        label: formatProductName(item.name),
      })),
    [items]
  );

  const sortConfig = (sortParamValue: string) => ({
    sortSearchKey: "sortAttribute",
    sortOrderSearchKey: "sortOrder",
    sortParamValue,
  });

  const header: TableHeaderType<TableBreakdownItem>[] = [
    {
      id: 1,
      uid: "name",
      label: columnLabel,
      filter: {
        filterType: FilterType.DROPDOWN,
        options: nameOptions,
        value: nameFilter,
        onChange: onFilterChange,
        searchBy: "label",
      },
      sort: sortConfig("name"),
      render: item => <Typography size="sm">{formatProductName(item.name)}</Typography>,
    },
    {
      id: 2,
      uid: "total_dbus",
      label: "DBUs",
      sort: sortConfig("total_dbus"),
      render: item => (
        <Typography size="sm">
          {item.total_dbus.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </Typography>
      ),
    },
    {
      id: 3,
      uid: "estimated_cost",
      label: "Total Cost",
      sort: sortConfig("estimated_cost"),
      render: item => (
        <Typography size="sm">
          {formatNumber(item.estimated_cost, { currency, notation: "standard" })}
        </Typography>
      ),
    },
    {
      id: 4,
      uid: "pct_of_total",
      label: "% of Total",
      sort: sortConfig("pct_of_total"),
      render: item => <Typography size="sm">{item.pct_of_total.toFixed(1)}%</Typography>,
    },
  ];

  return (
    <Stack direction="column" gap="sm">
      {nameFilter.length > 0 && (
        <Stack direction="row" gap="sm" wrap className="al-mb-2">
          {nameFilter.map(name => (
            <Badge key={name} variant="outline" className="al-gap-1">
              {columnLabel}: {formatProductName(name)}
              <Tooltip title={`Remove ${formatProductName(name)} filter`}>
                <Button
                  variant="ghost"
                  size="icon"
                  title={`Remove ${formatProductName(name)} filter`}
                  aria-label={`Remove ${formatProductName(name)} filter`}
                  className="al-ml-1 al-h-4 al-w-4 al-p-0 al-text-gray-500 hover:al-text-gray-700"
                  onClick={() => onRemoveFilter(name)}
                >
                  <IconX size={12} />
                </Button>
              </Tooltip>
            </Badge>
          ))}
        </Stack>
      )}
      <DataTable<TableBreakdownItem> header={header} data={{ items: tableData }} />
    </Stack>
  );
};

export default CostBreakdownTable;
